import { type BaseSyntheticEvent, useEffect, useState, useRef } from 'react'
import { sanitizeHash } from './url'

export function getHeaderHeight() {
  if (typeof document === 'undefined') return 0
  const header = document.querySelector('header')
  return header?.offsetHeight ?? 0
}

/**
 * Get the scroll position of an element relative to the top of the document,
 * accounting for any fixed headers.
 */
export function getScrollPosition(element: HTMLElement) {
  if (!element) return 0

  const headerHeight = getHeaderHeight()
  const rect = element.getBoundingClientRect()

  const currentScroll = window.pageYOffset || document.documentElement.scrollTop
  return rect.top + currentScroll - headerHeight
}

export function scrollToElement(element: HTMLElement, offset: number = 0, options: { behavior?: ScrollBehavior } = {}) {
  if (element) {
    // Show element if it's hidden
    if (element && getComputedStyle(element)?.display === 'none') {
      element.style.display = 'block'
    }

    const elementPosition = getScrollPosition(element)

    window.scrollTo({
      top: elementPosition - offset,
      behavior: options.behavior || 'smooth',
    })
  }
}

export function smoothScroll(event: BaseSyntheticEvent, offset: number = 0, shouldReplaceHistory = true) {
  const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style
  if (isSmoothScrollSupported) {
    event.preventDefault()
    let { target } = event
    while (target && !target.hash) target = target.parentElement

    let hash = decodeURIComponent(target?.hash)
    if (hash === 'undefined') {
      hash = `#${event.target.getAttribute('id')}`
    }

    const element = hash ? document.getElementById(sanitizeHash(hash)) : null
    if (element) {
      scrollToElement(element, offset)

      if (shouldReplaceHistory) {
        window.history.replaceState(
          { documentScrollPosition: { top: getScrollPosition(element) - offset, left: 0 } },
          '',
          hash
        )
      }
    }
  }
}

export function isScrolledToTop() {
  return window.scrollY === 0 || document.documentElement.scrollTop === 0
}

export function useScrollSpy({ id }: { id: string }) {
  useEffect(() => {
    const scrollables = document.querySelectorAll('.scrollspy')
    const navLinks = document.querySelectorAll(`#${id} a`)
    const header = document.querySelector('header')

    if (scrollables.length === 0 || navLinks.length === 0) return

    // Function to update active state
    const updateActiveState = (targetId: string | null) => {
      // Remove active class from all nav links
      navLinks.forEach((link) => link.parentElement?.classList.remove('scrollactive'))

      if (targetId) {
        // Add active class to matching link
        navLinks.forEach((link) => {
          if (link.getAttribute('href') === `#${targetId}`) {
            // add scrollactive to its parent
            link.parentElement?.classList.add('scrollactive')
          }
        })
      }
    }

    // Get section positions and find the active one based on scroll position
    const findActiveSection = () => {
      const scrollTop = window.scrollY
      let activeSection: string | null = null

      // Convert to array and get section data
      const sections = Array.from(scrollables).map((section) => {
        const rect = (section as HTMLElement).getBoundingClientRect()
        const top = rect.top + window.scrollY - (header?.offsetHeight ?? 0) - 5 // Get absolute position from top of document

        return {
          id: section.id,
          element: section as HTMLElement,
          top: top,
          bottom: top + rect.height,
        }
      })

      // Sort by position
      sections.sort((a, b) => a.top - b.top)

      // Find which section we're currently in
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const sectionStart = section.top
        // const sectionEnd = section.bottom

        // If we're past this section's start
        if (scrollTop >= sectionStart) {
          activeSection = section.id
        }

        // If we haven't reached the first section yet, clear active state
        if (i === 0 && scrollTop < sectionStart) {
          activeSection = null
        }
      }

      return activeSection
    }

    // Handle scroll events
    const handleScroll = () => {
      const activeSection = findActiveSection()
      updateActiveState(activeSection)
    }

    // Set initial state
    handleScroll()

    // Add scroll listener with throttling for performance
    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    // Cleanup function
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
  }, [id])
}

export function downloadBlob(content: any, type: string, filename: string) {
  const blob = new Blob([content], { type })
  const link = document.createElement('a')

  link.download = filename
  link.href = window.URL.createObjectURL(blob)
  link.dataset.downloadurl = [type, link.download, link.href].join(':')

  link.dispatchEvent(
    new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    })
  )
  link.remove()
}

export function selectElementText(elId: string) {
  const el = document.getElementById(elId)
  const sel = window.getSelection()
  if (sel) sel.removeAllRanges()
  if (el) {
    const range = window.document.createRange()
    if (range) {
      range.selectNodeContents(el)
      if (sel) sel.addRange(range)
    }
  }
}

export function useHasScrolled(distance = '10px') {
  const topRef = useRef(null)
  const [hasScrolled, setHasScrolled] = useState(false)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        setHasScrolled(!entry.isIntersecting)
      },
      { rootMargin: distance }
    )

    if (topRef.current) {
      observer.observe(topRef.current)

      return () => {
        if (topRef.current) observer.unobserve(topRef.current)
      }
    }
  }, [])
  return [topRef, hasScrolled]
}
